import { memo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Bookmark, BookmarkCheck, CalendarClock, Check, Heart, Info, Play } from 'lucide-react';
import {
  formatRuntime,
  type MediaSummary,
  type MovieDetails,
  type SeriesDetails,
} from '@teaplus/shared';
import { cn } from '@/lib/cn';
import { api } from '@/lib/api';
import { useFavouriteToggle } from '@/lib/use-favourites';
import { useWatchlistToggle } from '@/lib/use-watchlist';
import { usePartyLookers } from '@/lib/party';
import { comingSoon } from '@/lib/coming-soon';
import { qk, useProfileId, useWatchedIds } from '@/lib/queries';
import { MediaContextMenu } from '../context-menus';
import { ProfileAvatar } from '../profile/avatar';

export const detailsPath = (m: Pick<MediaSummary, 'id' | 'type'>) =>
  m.type === 'movie' ? `/movie/${m.id}` : `/series/${m.id}`;

export function Poster({
  src,
  alt,
  className,
  eager,
}: {
  src: string | null;
  alt: string;
  className?: string;
  eager?: boolean;
}) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div className={cn('relative overflow-hidden bg-card', className)}>
      {src && !failed ? (
        <img
          src={src}
          alt={alt}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          onLoad={() => setLoaded(true)}
          onError={() => setFailed(true)}
          className={cn(
            'h-full w-full object-cover transition-[opacity,transform] duration-500 ease-[var(--ease-out-soft)]',
            loaded ? 'opacity-100' : 'opacity-0',
          )}
        />
      ) : (
        <div className="grid h-full w-full place-items-center bg-gradient-to-br from-elevated to-card p-3 text-center">
          <span className="font-display text-sm font-semibold text-text/70">{alt}</span>
        </div>
      )}
      {src && !failed && !loaded && <div className="skeleton absolute inset-0" aria-hidden />}
    </div>
  );
}

/** The faces of people in your party who are looking at this title right now. */
function PartyLookers({ mediaId }: { mediaId: string }) {
  const lookers = usePartyLookers(mediaId);
  if (!lookers.length) return null;
  return (
    <span
      className="absolute right-2 top-2 flex -space-x-2"
      role="img"
      aria-label={`${lookers.map((m) => m.name).join(', ')} looking at this`}
    >
      {lookers.slice(0, 3).map((m) => (
        <ProfileAvatar
          key={m.id}
          avatar={m.avatar}
          image={m.avatarUrl}
          size={28}
          rounded="full"
          className="ring-2 ring-pink"
        />
      ))}
    </span>
  );
}

export interface CardExtras {
  progress?: number | null;
  caption?: string | null;
}

export const MediaCard = memo(function MediaCard({
  media,
  className,
  eager,
  progress,
  caption,
}: {
  media: MediaSummary;
  className?: string;
  eager?: boolean;
} & CardExtras) {
  const wl = useWatchlistToggle();
  const navigate = useNavigate();
  const watched = useWatchedIds().data?.has(media.id) ?? false;
  const inList = wl.has(media.id);
  const fav = useFavouriteToggle();
  const loved = fav.has(media.id);
  const [hovered, setHovered] = useState(false);
  const soon = comingSoon(media);
  const hoverTimer = useRef<number | null>(null);
  const onEnter = () => {
    hoverTimer.current = window.setTimeout(() => setHovered(true), 220);
  };
  const onLeave = () => {
    if (hoverTimer.current) window.clearTimeout(hoverTimer.current);
  };
  const lift = '';
  return (
    <MediaContextMenu media={media}>
      <div
        className={cn('group/card relative shrink-0', className)}
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
      >
        <div className="relative z-0 rounded-[var(--radius-card)] transition-transform duration-300 ease-[var(--ease-out-soft)] after:pointer-events-none after:absolute after:inset-0 after:rounded-[var(--radius-card)] after:opacity-0 after:shadow-[0_24px_48px_-16px_rgba(0,0,0,.9),0_0_0_1.5px_color-mix(in_oklab,var(--color-pink)_55%,transparent),0_0_32px_-6px_color-mix(in_oklab,var(--color-pink)_35%,transparent)] after:transition-opacity after:duration-300 group-hover/card:z-10 group-hover/card:-translate-y-1.5 group-hover/card:scale-[1.06] group-hover/card:after:opacity-100 motion-reduce:transform-none [@media(hover:none)]:transform-none">
          <Link
            to={detailsPath(media)}
            className="relative block rounded-[var(--radius-card)] outline-none focus-visible:ring-2 focus-visible:ring-pink focus-visible:ring-offset-2 focus-visible:ring-offset-bg"
            aria-label={`${media.title}${media.year ? ` (${media.year})` : ''}${watched ? ', watched' : ''}`}
          >
            <Poster
              src={media.posterUrl}
              alt={media.title}
              eager={eager}
              className="aspect-[2/3] rounded-[var(--radius-card)] ring-1 ring-white/5 [&_img]:transition-transform [&_img]:duration-700 [&_img]:ease-[var(--ease-out-soft)] [&_img]:group-hover/card:scale-[1.08] motion-reduce:[&_img]:transform-none"
            />
            {watched && (
              <span
                className={cn(
                  'absolute left-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-pink text-on-pink shadow-[0_2px_10px_rgba(0,0,0,.55)] ring-2 ring-black/25',
                  lift,
                )}
                title="Watched"
                data-testid="watched-badge"
              >
                <Check className="h-4 w-4" strokeWidth={3} aria-hidden />
              </span>
            )}
            <PartyLookers mediaId={media.id} />
            {soon && (
              <span className="absolute bottom-2 left-2 rounded-full bg-black/75 px-2.5 py-1 text-[11px] font-semibold text-pink-soft shadow-[0_2px_10px_rgba(0,0,0,.55)] backdrop-blur">
                Coming Soon
              </span>
            )}
            {progress != null && progress > 0 && progress < 1 && (
              <span
                className={cn(
                  'absolute inset-x-2.5 bottom-2.5 h-1 overflow-hidden rounded-full bg-white/25',
                  lift,
                )}
                aria-hidden
              >
                <span
                  className="block h-full rounded-full bg-pink"
                  style={{ width: `${Math.max(4, progress * 100)}%` }}
                />
              </span>
            )}
          </Link>

          {/* Clicks pass through to the poster's link: only the buttons below catch them. */}
          <div className="pointer-events-none absolute inset-x-0 bottom-0 hidden translate-y-2 rounded-b-[var(--radius-card)] bg-gradient-to-t from-black/95 via-black/75 to-transparent p-3 pt-14 opacity-0 transition-[opacity,transform] duration-300 ease-[var(--ease-out-soft)] group-hover/card:translate-y-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100 [@media(hover:hover)]:block">
            <p className="line-clamp-2 text-sm font-semibold leading-tight">{media.title}</p>
            <HoverMeta media={media} active={hovered} />
            <div className="@container -mx-1 mt-2.5 flex translate-y-1 items-center gap-1 opacity-0 transition-[opacity,transform] delay-75 duration-300 group-hover/card:translate-y-0 group-hover/card:opacity-100 group-focus-within/card:opacity-100 @[9.5rem]:gap-1.5">
              <CardAction
                label={soon ? `${media.title} is coming soon` : `Play ${media.title}`}
                tip={soon ? `Coming Soon${soon.on ? ` · ${soon.on}` : ''}` : 'Play'}
                primary
                onClick={() => navigate(detailsPath(media))}
              >
                {soon ? (
                  <CalendarClock className="h-4 w-4" />
                ) : (
                  <Play className="h-4 w-4 fill-current" />
                )}
              </CardAction>
              <CardAction
                label={
                  inList
                    ? `Remove ${media.title} from Watch Later`
                    : `Add ${media.title} to Watch Later`
                }
                tip={inList ? 'Remove from Watch Later' : 'Add to Watch Later'}
                onClick={() => wl.toggle(media)}
              >
                {inList ? (
                  <BookmarkCheck className="h-4 w-4 fill-current" />
                ) : (
                  <Bookmark className="h-4 w-4" />
                )}
              </CardAction>
              <CardAction
                label={
                  loved
                    ? `Remove ${media.title} from Favourites`
                    : `Add ${media.title} to Favourites`
                }
                tip={loved ? 'Remove from Favourites' : 'Add to Favourites'}
                onClick={() => fav.toggle(media)}
              >
                <Heart className={cn('h-4 w-4', loved && 'fill-pink text-pink')} />
              </CardAction>
              <Link
                to={detailsPath(media)}
                aria-label={`More about ${media.title}`}
                data-tip="More information"
                tabIndex={-1}
                className="pointer-events-auto ml-auto grid h-7 w-7 @[9.5rem]:h-8 @[9.5rem]:w-8 place-items-center rounded-full border border-white/25 bg-black/40 text-text/90 transition-colors hover:border-pink hover:text-pink"
              >
                <Info className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>

        <div className={cn('mt-2', caption === undefined && '[@media(hover:hover)]:hidden')}>
          <p className="line-clamp-1 text-[13px] font-medium text-text/85">{media.title}</p>
          {caption && <p className="mt-0.5 line-clamp-1 text-xs text-muted">{caption}</p>}
        </div>
        <button
          onClick={() => wl.toggle(media)}
          aria-label={
            inList ? `Remove ${media.title} from Watch Later` : `Add ${media.title} to Watch Later`
          }
          className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-full bg-black/55 backdrop-blur [@media(hover:hover)]:hidden"
        >
          {inList ? (
            <BookmarkCheck className="h-4 w-4 fill-current text-pink" />
          ) : (
            <Bookmark className="h-4 w-4" />
          )}
        </button>
        <button
          onClick={() => fav.toggle(media)}
          aria-label={
            loved ? `Remove ${media.title} from Favourites` : `Add ${media.title} to Favourites`
          }
          className="absolute right-2 top-12 grid h-8 w-8 place-items-center rounded-full bg-black/55 backdrop-blur [@media(hover:hover)]:hidden"
        >
          <Heart className={cn('h-4 w-4', loved && 'fill-pink text-pink')} />
        </button>
      </div>
    </MediaContextMenu>
  );
});

function HoverMeta({ media, active }: { media: MediaSummary; active: boolean }) {
  return (
    <div className="mt-1 flex min-h-4 items-center gap-1.5 text-xs text-text/70">
      {media.year && <span>{media.year}</span>}
      {/* Only cards that have been hovered ask for their details: a page of cards stays light. */}
      {active && <HoverInfo media={media} />}
      {media.certification && (
        <span className="ml-auto rounded border border-white/25 px-1 text-[10px] font-semibold">
          {media.certification}
        </span>
      )}
    </div>
  );
}

/** Runtime for a film, number of seasons for a show; fetching them also readies the title page. */
function HoverInfo({ media }: { media: MediaSummary }) {
  const p = useProfileId();
  const movie = media.type === 'movie';
  const q = useQuery({
    queryKey: movie ? qk.movie(p, media.id) : qk.series(p, media.id),
    queryFn: () =>
      api<MovieDetails | SeriesDetails>(`/api/content/${movie ? 'movies' : 'series'}/${media.id}`),
    enabled: !!p,
    staleTime: 10 * 60_000,
    retry: false,
  });
  let info: string | null = null;
  if (q.data && movie) info = formatRuntime((q.data as MovieDetails).runtime);
  if (q.data && !movie) {
    const seasons = (q.data as SeriesDetails).seasons.filter((s) => s.seasonNumber > 0);
    info = seasons.length ? `${seasons.length} season${seasons.length === 1 ? '' : 's'}` : null;
  }
  return info ? (
    <>
      <span aria-hidden>·</span>
      <span className="animate-[fade-in_250ms_ease-out]">{info}</span>
    </>
  ) : q.isFetching ? (
    <span className="skeleton inline-block h-2.5 w-14 rounded" aria-hidden />
  ) : null;
}

function CardAction({
  children,
  label,
  tip,
  onClick,
  primary,
}: {
  children: React.ReactNode;
  label: string;
  tip: string;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      data-tip={tip}
      tabIndex={-1}
      className={cn(
        'pointer-events-auto grid h-7 w-7 @[9.5rem]:h-8 @[9.5rem]:w-8 place-items-center rounded-full transition-[background,color,transform] active:scale-90',
        primary
          ? 'bg-pink text-on-pink hover:bg-pink-hover'
          : 'border border-white/25 bg-black/40 hover:border-pink hover:text-pink',
      )}
    >
      {children}
    </button>
  );
}
